import Skeleton from '../Skeleton/Skeleton';
import clsx from 'clsx';
import React from 'react';
import { formatNumber, parseToDecimal } from '../utils/formatters';
import Decimal from 'decimal.js';
import { Typography } from '../Typography/Typography';
import { TypographyFontWeightModel } from '../Typography/tools/Typography.model';
import { FontSizeType } from '../styles/functions';
import { Delimiters } from 'common-react';
import { Theme, useTheme } from '../styles/styles';
import { ColorPath } from '../theme/tools/theme.model';

import useStyles from './NumberValue.style';

interface BasicNumberValueProps {
  decimalScale: number;
  fixedDecimalScale?: boolean;
  income?: boolean;
  showSignWithoutColours?: boolean;
  suffix?: string;
  autoTestId?: string;
  valueSize?: FontSizeType;
  valueClassName?: string;
  codeSize?: FontSizeType;
  codeClassName?: string;
  fontWeight?: TypographyFontWeightModel;
  valueFontWeight?: TypographyFontWeightModel;
  codeFontWeight?: TypographyFontWeightModel;
}

export interface NumberValueProps extends BasicNumberValueProps {
  value?: Decimal | number;
  classNameSkeleton?: string;
  valueSmall?: boolean;
  labelSmall?: boolean;
}

export interface FormattedNumberProps extends BasicNumberValueProps {
  value: Decimal;
}

const numberMark = (value: Decimal): string => {
    if (value.greaterThan(0)) return `${Delimiters.PLUS_SIGN}`;
    if (value.lessThan(0)) return `${Delimiters.DASH}`;
    return '';
};

const getIncomeTextColor = (
  value: Decimal,
  theme: Theme,
  income?: boolean,
): ColorPath | undefined => {
  if (!income) return undefined;
  return value.lessThan(0) ? theme.color.reds[100] as ColorPath : theme.color.greens[100] as ColorPath;
};

const FormattedNumber: React.FC<FormattedNumberProps> = ({
  value,
  decimalScale,
  fixedDecimalScale,
  income,
  showSignWithoutColours,
  suffix,
  autoTestId,
  valueClassName,
  codeClassName,
  fontWeight,
  valueFontWeight,
  codeFontWeight,
}) => {
  const theme = useTheme();
  const formatted = formatNumber(value, decimalScale, fixedDecimalScale);
  const textColor = getIncomeTextColor(value, theme, income);
  const shouldUseMark = income || showSignWithoutColours;
  const sign = value.lessThan(0) ? `${Delimiters.DASH}` : '';

  const displayValue = shouldUseMark
  ? `${numberMark(value)}${formatted}`
  : `${sign}${formatted}`;

  return (
    <Typography
      component="span"
      className={valueClassName}
      data-autotestid={autoTestId}
      textColor={textColor}
      fontWeight={valueFontWeight || fontWeight}
    >
      {displayValue}
      <Typography
        component="span"
        className={codeClassName}
        textColor={textColor}
        fontWeight={codeFontWeight || fontWeight}
      >
        {` ${suffix}`}
      </Typography>
    </Typography>
  );
};

const NumberValue: React.FC<NumberValueProps> = ({
  value,
  decimalScale,
  fixedDecimalScale = true,
  income,
  showSignWithoutColours = false,
  suffix,
  valueSize,
  codeSize,
  valueClassName,
  codeClassName,
  classNameSkeleton,
  autoTestId,
  fontWeight,
  valueFontWeight,
  codeFontWeight,
}) => {

  const decimalValue = parseToDecimal(value);

  const c = useStyles({ value: decimalValue, decimalScale, income, valueSize, codeSize });

  const renderFormattedNumber = (numberValue: Decimal) => {
    return <FormattedNumber
              valueClassName={clsx(c.value, valueClassName)}
              codeClassName={clsx(c.code, codeClassName)}
              value={numberValue}
              decimalScale={decimalScale}
              fixedDecimalScale={fixedDecimalScale}
              income={income}
              showSignWithoutColours={showSignWithoutColours}
              suffix={suffix}
              autoTestId={autoTestId}
              fontWeight={fontWeight}
              valueFontWeight={valueFontWeight}
              codeFontWeight={codeFontWeight}
            />;
  };

  if (decimalValue === undefined) {
    return (
        <Skeleton variant="text" animation="wave" className={clsx(c.skeleton, classNameSkeleton)}>
          {renderFormattedNumber(new Decimal(0))}
        </Skeleton>
    );
  }

  return renderFormattedNumber(decimalValue);
};

export { NumberValue };

